import React, { useCallback, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Download, MessageCircle } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/omega/Reveal";
import PhotoImage from "../components/fotografia/PhotoImage";
import ExperienciaForm, { experienciaGuardada } from "../components/fotografia/ExperienciaForm";
import DescargaModal, { enlaceRecordado } from "../components/fotografia/DescargaModal";
import { entregaPorPath } from "../data/entregas";
import { SITE_NAME } from "../seo/config";
import { SIZES } from "../utils/photos";
import { buildWhatsAppUrl } from "../utils/whatsapp";

/**
 * Página de entrega privada a un cliente.
 *
 * Misma gramática que el resto del sitio: las fotos sobre el campo oscuro
 * (navy) y el texto que hay que leer — cómo descargar — sobre papel (sand).
 *
 * Tres decisiones que no son de estilo:
 *
 * 1. noindex. Es un encargo privado; la página existe solo para quien tiene
 *    el enlace. public/_headers repite la orden en la cabecera HTTP, que es
 *    la que respetan también las imágenes.
 * 2. El enlace de WhatsApp de aquí NO pasa por trackWhatsAppLead: un cliente
 *    que pregunta por su descarga no es un lead y no debe contar como
 *    conversión en Google Ads. Por lo mismo el botón flotante y la barra de
 *    precios se ocultan en estas rutas (WhatsAppFab, MobileActionBar).
 * 3. La galería es una muestra. El botón de descarga lleva a la carpeta con
 *    la entrega completa en alta resolución, que es lo que el cliente guarda,
 *    pero solo con la clave de la entrega (DescargaModal): el enlace no está
 *    en el sitio. Una vez puesta, el navegador la recuerda y el botón pasa a
 *    ser un enlace directo.
 */

const SIZES_ANCHA = "(min-width: 1152px) 1152px, 100vw";

const MENSAJE_AYUDA = "Hola Omega, tengo una consulta sobre la descarga de mis fotos.";

const BotonDescarga = ({ enlace, onPedirClave, variante = "claro", className = "" }) => {
  const clase = `${variante === "claro" ? "btn-on-dark" : "btn-omega"} ${className}`;
  const contenido = (
    <>
      <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
      Descarga tus fotos
    </>
  );
  return enlace ? (
    <a href={enlace} target="_blank" rel="noopener noreferrer" className={clase}>
      {contenido}
    </a>
  ) : (
    <button type="button" onClick={onPedirClave} className={clase}>
      {contenido}
    </button>
  );
};

const EntregaPage = () => {
  const { pathname } = useLocation();
  const entrega = entregaPorPath(pathname);
  const [enviada, setEnviada] = useState(() => Boolean(entrega && experienciaGuardada(entrega.path)));
  const [enlace, setEnlace] = useState(() => (entrega ? enlaceRecordado(entrega.path) : ""));
  const [pidiendoClave, setPidiendoClave] = useState(false);
  const cerrarModal = useCallback(() => setPidiendoClave(false), []);
  const pedirClave = () => setPidiendoClave(true);

  if (!entrega) return <Navigate to="/" replace />;

  const titulo = `${entrega.titulo} ${entrega.fecha.replace(/ · /g, "-")}`;

  return (
    <>
      <Seo
        title={`${titulo} | ${SITE_NAME}`}
        description={`Entrega privada de fotografías: ${entrega.titulo.toLowerCase()} del ${entrega.fecha.replace(/ · /g, "-")}.`}
        path={entrega.path}
        noindex
      />

      <section className="bg-navy-900 pb-12 pt-28 text-white md:pb-16 md:pt-32">
        <div className="container-page">
          <div className="hero-enter max-w-3xl">
            <p className="text-sand-300">Entrega privada</p>
            <h1 className="mt-4 font-display text-display-md font-bold text-white">{entrega.titulo}</h1>
            <p className="mt-3 font-mono text-sm uppercase tracking-label text-navy-200">{entrega.fecha}</p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-navy-100">
              Una selección de su día. Todas las fotografías, editadas y en alta resolución, están
              listas para descargar.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <BotonDescarga enlace={enlace} onPedirClave={pedirClave} />
              <a href="#galeria" className="btn-on-dark-outline">
                Ver la selección
              </a>
            </div>
          </div>

          <figure className="mt-12 md:mt-16">
            <PhotoImage
              foto={entrega.portada}
              sizes={SIZES_ANCHA}
              priority
              className="h-auto w-full"
            />
          </figure>
        </div>
      </section>

      <section id="galeria" className="bg-navy-900 pb-20 md:pb-28">
        <div className="container-page">
          <div className="grid gap-4 sm:grid-cols-2">
            {entrega.fotos.map((item) => (
              <Reveal
                as="figure"
                key={item.id}
                y={16}
                amount={0.15}
                className={item.ancha ? "sm:col-span-2" : ""}
              >
                <PhotoImage
                  foto={item}
                  sizes={item.ancha ? SIZES_ANCHA : SIZES.grid2}
                  className="h-auto w-full"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="descarga" className="bg-sand-50 py-20 md:py-28">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
          <div>
            <h2 className="section-title">Tus fotos, en alta resolución.</h2>
            <p className="section-copy mt-5 max-w-md">
              La entrega completa está en una carpeta compartida. Ábrela con la clave que te enviamos,
              descárgala y guarda una copia en tu computador o en tu nube: es la forma más segura de
              conservarlas.
            </p>
            <BotonDescarga enlace={enlace} onPedirClave={pedirClave} variante="oscuro" className="mt-8" />
          </div>

          <div className="surface-card p-6 sm:p-8">
            <h3 className="font-display text-xl font-bold text-navy-900">Cómo descargarlas</h3>
            <ol className="mt-5 space-y-4 text-navy-600">
              <li>
                <span className="font-semibold text-navy-900">En el computador:</span> abre el enlace y
                pulsa «Descargar todo». Llegan en un archivo .zip; ábrelo para ver las fotos.
              </li>
              <li>
                <span className="font-semibold text-navy-900">En el celular:</span> abre el enlace en la
                app de Google Drive, elige una foto y guárdala desde el menú de los tres puntos.
              </li>
            </ol>
            <p className="mt-6 border-t border-navy-100 pt-5 text-sm text-navy-500">
              ¿Algún problema con la descarga?{" "}
              <a
                href={buildWhatsAppUrl(MENSAJE_AYUDA)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-navy-900 underline decoration-sand-400 underline-offset-4 hover:text-navy-600"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                Escríbenos por WhatsApp
              </a>
            </p>
          </div>
        </div>
      </section>

      <section id="experiencia" className="border-t border-navy-100 bg-sand-100 py-20 md:py-28">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
          <div>
            <h2 className="section-title">
              {enviada ? "Gracias por tu experiencia." : "¿Cómo fue tu experiencia?"}
            </h2>
            <p className="section-copy mt-5 max-w-md">
              {enviada
                ? "Ya la recibimos. Si quieres agregar algo, escríbenos por WhatsApp."
                : "Nos toma un minuto leerte y nos ayuda a mejorar cada entrega. Solo las estrellas son obligatorias."}
            </p>
          </div>
          <div className="surface-card relative p-6 sm:p-8">
            <ExperienciaForm entrega={entrega.path} onEnviada={() => setEnviada(true)} />
          </div>
        </div>
      </section>

      <DescargaModal
        entrega={entrega.path}
        abierto={pidiendoClave}
        onCerrar={cerrarModal}
        onDesbloqueada={setEnlace}
      />
    </>
  );
};

export default EntregaPage;
